import { useEffect, useState } from 'react';
import { useV07Commands } from './commands-context';
import { useDemoData } from './store';
import type { DemoCredential } from './types';

/**
 * Where the «Hedef e-posta» line in the resend window gets its address from.
 *
 * WHY THIS EXISTS
 * ---------------
 * The register's rows come from the list projection, which deliberately does not carry a recipient's
 * contact address — a page of two hundred rows has no business downloading two hundred private
 * addresses, and the column that looks like an e-mail column is the delivery STATE. So the window,
 * opened from that register, printed an empty target and offered to send to it. The address is read
 * here, from the authorized single-record read the detail screen already uses, and never from the
 * public API: `credential.read` is the command that decides who may see it.
 *
 * WHY FOUR STATES AND NOT A STRING
 * --------------------------------
 * «Still loading», «this record has no address on file» and «the address could not be read» are
 * three different answers, and an empty string says all three at once. The send button is enabled
 * only for the fourth — a real address — because a send with no known target is a send to nowhere.
 */
export type ResendTarget =
  | { status: 'loading'; email: null }
  | { status: 'ready'; email: string }
  | { status: 'absent'; email: null }
  | { status: 'unreadable'; email: null };

export function useResendTarget(credential: DemoCredential): ResendTarget {
  const commands = useV07Commands();
  const db = useDemoData();
  const stored = db.credentials.find((c) => c.id === credential.id);
  /* The store's copy wins only when it is the RICHER one: the register's projection has no address. */
  const known = stored?.privateContactEmail ?? credential.privateContactEmail ?? null;
  const [phase, setPhase] = useState<'reading' | 'read' | 'failed'>(known ? 'read' : 'reading');
  const code = credential.publicCode;

  useEffect(() => {
    if (known) return undefined;
    let live = true;
    setPhase('reading');
    void commands
      .hydrateCredential({ code })
      .then((r) => {
        if (live) setPhase(r.ok ? 'read' : 'failed');
      })
      .catch(() => {
        if (live) setPhase('failed');
      });
    return () => {
      live = false;
    };
    /* `known` is in the list and guarded at the top: once the address arrives this re-runs once and
       returns immediately, which is the honest way to say «nothing left to read». */
  }, [code, commands, known]);

  if (known) return { status: 'ready', email: known };
  if (phase === 'failed') return { status: 'unreadable', email: null };
  if (phase === 'reading') return { status: 'loading', email: null };
  return { status: 'absent', email: null };
}

/** What the window prints when there is no address to print. */
export const RESEND_TARGET_TEXT: Record<Exclude<ResendTarget['status'], 'ready'>, string> = {
  loading: 'Okunuyor…',
  absent: 'Bu kayıtta iletişim e-postası yok',
  unreadable: 'Hedef adres okunamadı',
};
